import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import axios from 'axios';
import { dv } from '../i18n/dv.ts';

type ErrorKind = 'expired' | 'generic' | 'mismatch' | null;

export function ResetPassword(): JSX.Element {
  const { token } = useParams({ from: '/reset-password/$token' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ErrorKind>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('mismatch');
      return;
    }
    setPending(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 400 || status === 404) {
        setError('expired');
      } else {
        setError('generic');
      }
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12">
        <div className="rounded-3xl bg-[hsl(var(--chrome))] p-6 lg:p-7 space-y-5 text-center">
          <div className="mx-auto grid size-14 place-items-center rounded-full bg-[hsl(var(--success-container))] text-[hsl(var(--on-success-container))]">
            <Icon name="check_circle" size={28} fill />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{dv.auth.passwordChanged}</h1>
          <Link to="/login" className="btn-primary w-full">
            {dv.auth.login}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <div className="rounded-3xl bg-[hsl(var(--chrome))] p-6 lg:p-7 space-y-6">
        <h1 className="text-2xl font-bold tracking-tight">{dv.auth.resetPassword}</h1>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <label htmlFor="rp-password" className="field-label">{dv.auth.newPassword}</label>
            <input
              id="rp-password"
              className="input"
              type="password"
              dir="ltr"
              aria-label={dv.auth.newPassword}
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rp-confirm" className="field-label">{dv.auth.confirmPassword}</label>
            <input
              id="rp-confirm"
              className="input"
              type="password"
              dir="ltr"
              aria-label={dv.auth.confirmPassword}
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error === 'mismatch' && (
            <div className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive" role="alert">
              {dv.auth.passwordMismatch}
            </div>
          )}
          {error === 'expired' && (
            <div className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive" role="alert">
              {dv.auth.resetLinkExpired}{' '}
              <Link to="/forgot-password" className="underline underline-offset-4 hover:opacity-80">
                {dv.auth.sendResetLink}
              </Link>
            </div>
          )}
          {error === 'generic' && (
            <div className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive" role="alert">
              {dv.common.error}
            </div>
          )}
          <button
            type="submit"
            disabled={pending}
            className="btn-primary w-full"
          >
            {pending ? dv.common.loading : dv.auth.resetPassword}
          </button>
        </form>
      </div>
    </div>
  );
}
